import { postData } from "../../../components/utilities/UtilityFunctions";
import { postNotification } from "../../../components/utilities/Notifications";

// The admin / dev tools in the event Options menus that fill in a round with random data (BCP-529, BCP-530,
// BCP-531). Each score entry has its own `active` flag: an inactive entry is shown greyed out and does nothing,
// but its handler is already wired with the right pairingType, so switching it on is changing the flag here.
export const RANDOM_TOOLS = {
  playerMatches: { active: true, pairingType: "Pairing" },
  teamMatches: { active: false, pairingType: "TeamPairing" },
  doublesMatches: { active: false, pairingType: "TeamPairing" },
  podMatches: { active: false, pairingType: "SeatedPairing" },
  teamPairings: { active: true }
};

const formatOf = (event) => ({
  team: Boolean(event?.format?.teamEvent ?? event?.teamEvent),
  doubles: Boolean(event?.format?.doublesEvent ?? event?.doublesEvent),
  // seated (pod) pairings are the board game events' pairings
  seated: Boolean(event?.format?.boardGameEvent ?? event?.boardGameEvent)
});

// the translation key of a tool's menu label: playerMatches -> submitRandomPlayerMatches
export const toolLabelKey = (key) =>
  `submitRandom${key.charAt(0).toUpperCase()}${key.slice(1)}`;

// Which random tools apply to this kind of event, in menu order. Each is { key, pairingType? } and its label is
// the translation key `submitRandom<Key>`.
export const randomToolsFor = (event) => {
  const { team, doubles, seated } = formatOf(event);
  const tools = [];
  if (!doubles && !seated) tools.push("playerMatches");
  if (team && !doubles) tools.push("teamMatches");
  if (doubles) tools.push("doublesMatches");
  if (seated) tools.push("podMatches");
  if (team && !doubles) tools.push("teamPairings");
  return tools.map((key) => ({ key, ...RANDOM_TOOLS[key] }));
};

// Who is shown the random tools: platform admins, admins of the event's game system, and in dev any organizer
// (the Options menus are only shown to organizers). The API is the real gate: it answers 403 when this guess is
// wrong, for example when the deploy stage and the API's test environment disagree.
export const canUseRandomTools = (identity, event, isDevStage) =>
  identity?.isAdmin === true ||
  Boolean(
    event?.gameSystem?.id &&
      identity?.adminGameSystems?.includes(event.gameSystem.id)
  ) ||
  Boolean(isDevStage);

// Auto Pair is for team pairings of non-doubles team events that have no player pairings yet
export const canAutoPair = (event, teamPairing) =>
  formatOf(event).team &&
  !formatOf(event).doubles &&
  Boolean(teamPairing?.id) &&
  !teamPairing?.pairings?.length;

// the API's error code (unsupportedPairingType, maxScoreRequired, randomNotAllowed, ...) when it sent one
export const apiErrorCode = (err) => err?.data?.code;
export const isUnsupportedPairingType = (err) =>
  apiErrorCode(err) === "unsupportedPairingType";

// The body of POST /v1/pairings/randomScoreSubmission from the dialog's values (blank min / max are left out)
export const randomScoresBody = ({ eventId, pairingType, values }) => {
  const body = {
    eventId,
    pairingType,
    round: Number(values.round),
    scoreByes: values.scoreByes !== false,
    resubmitScores: values.resubmitScores === true
  };
  for (const key of ["minScore", "maxScore"]) {
    if (values[key] !== undefined && values[key] !== "") {
      body[key] = Number(values[key]);
    }
  }
  return body;
};

export const submitRandomScores = (body) =>
  postData("/pairings/randomScoreSubmission", { body });

export const submitRandomTeamPairings = (eventId) =>
  postData("/pairings/randomTeamPairings", { body: { eventId } });

export const autoPairTeamPairing = (teamPairingId, random = false) =>
  postData(`/pairings/${teamPairingId}/autoPair`, {
    body: { pairingType: "TeamPairing", ...(random ? { random: true } : {}) }
  });

// Shows a failed random tool call. A valid request the tool does not handle yet (unsupportedPairingType) is an
// information notice, not an error; everything else shows the API's own message (maxScoreRequired, 403, ...).
export const notifyRandomToolError = (err) => {
  const message = err?.data?.error || err;
  postNotification(isUnsupportedPairingType(err) ? "info" : "error", message);
};
